'use client';

import type { OverlayTriggerState } from '@react-stately/overlays';

import { ArrowDownSmall, WalletRegular } from '@zoomit/icons';
import { cn, Image, Popover } from '@zoomit/primitives';
import { InstallmentProviderItem, ProductPrice, Score, useHistoryState } from '@zoomit/web-kit';
import { Button, Hr, Typography } from '@zoomit/zoomit-ds';

import { useOrigin } from '../../providers/OriginProvider';
import type { PhysicalStoreProductItem } from '../../types/product';
import { PhysicalStoreListItemModal } from './PhysicalStoreListItemModal';

type Props = PhysicalStoreProductItem;

export const PhysicalStoreListItemMobile = (props: Props) => {
  const {
    cover,
    description,
    guarantee,
    id,
    isHybridStore,
    installmentProviders,
    userReviewsInfo,
    hasOutdatedPrice,
    storeTitle,
    storeId,
    price,
  } = props;

  const openContactInfo = useHistoryState<OverlayTriggerState>({});
  const { baseMediaUrl } = useOrigin();

  return (
    <div className="bg-card shadow-main flex flex-col rounded-sm p-4">
      <div className="flex w-full items-center justify-between" role="button" tabIndex={0}>
        <div className="p flex w-full flex-col items-center gap-4">
          <div className="relative flex h-fit w-full place-content-center justify-start gap-4">
            <Image
              src={`${baseMediaUrl}/${cover?.id}`}
              width={64}
              height={64}
              alt={cover?.alt || 'physical store cover'}
              style={{ objectFit: 'cover' }}
              placeholder="blur"
              className="h-16 w-16"
              blurDataURL={cover?.preview}
              sizes="96px"
            />
            <div className="flex flex-col items-start justify-center gap-2">
              <Typography tag="span" fontSize={1.4} fontWeight="bold" lineHeight={1.8}>
                {storeTitle}
              </Typography>
              {!!userReviewsInfo.averageScore && (
                <Score score={userReviewsInfo.averageScore} reviewCounts={userReviewsInfo.usersCount} />
              )}
            </div>
          </div>
          <div className="flex w-full flex-col flex-nowrap gap-2">
            <Typography variant="callout" className="fa text-grey1 line-clamp-1" lineHeight={1.8}>
              {description}
            </Typography>
            <Typography variant="callout" className="fa text-grey1 line-clamp-1" lineHeight={1.8}>
              {guarantee}
            </Typography>
            {props.installmentProviders && (
              <Popover
                className={'w-fit'}
                placement="top-end"
                content={
                  <div className="bg-background2 relative flex w-[328px] flex-col">
                    {props.installmentProviders.map(({ id, logo, title, description, url }, index) => (
                      <>
                        <InstallmentProviderItem
                          key={id}
                          provider={{
                            logo: {
                              alt: logo.alt ?? title,
                              src: `${baseMediaUrl}/${logo.id}`,
                              blurDataUrl: logo.preview,
                            },
                            url,
                            description,
                            title,
                          }}
                        />
                        {index !== installmentProviders?.length - 1 && <Hr />}
                      </>
                    ))}
                  </div>
                }
              >
                <div className="bg-grey6 text-subhead text-text border-borderPrimary my-2 flex w-fit items-center gap-1 rounded-full border px-2 leading-[2]">
                  <WalletRegular size="sm" fill="#F90" />
                  امکان خرید قسطی
                  <ArrowDownSmall size="xs" />
                </div>
              </Popover>
            )}
          </div>
          <div className="flex w-full items-center justify-between">
            <div className={cn('relative flex min-w-[102px] items-center gap-1 whitespace-nowrap')}>
              <ProductPrice price={price} />
            </div>
            <Button onClick={openContactInfo.open} className="text-subhead-bold w-fit">
              اطلاعات تماس
            </Button>
          </div>
        </div>
      </div>
      {openContactInfo.isOpen && (
        <PhysicalStoreListItemModal
          id={id}
          isHybridStore={isHybridStore}
          storeId={storeId}
          openContactInfo={openContactInfo}
        />
      )}
    </div>
  );
};
